/**
 * Portable region router — designed to be shared, byte-identical between this repo's own
 * `live/ofhunger-us` branch (us.ofhunger.com, bucket "na") and the separately-managed Global
 * theme (www.ofhunger.com, bucket "www"; historically called "the Global theme" before
 * `live/ofhunger-global` existed — see the `markets` note below for why that name now also
 * means something else). Do not add anything here that depends on this repo's import map,
 * analytics stack, or other branch-specific wiring — the whole point of this file is that it can
 * be copied verbatim between sites and just work, because `currentSiteKey()` resolves "which
 * site am I / where's the other one" from `location.hostname` at runtime instead of a
 * hardcoded per-repo constant.
 *
 * 2026-09-22: `live/ofhunger-global` added per-site `markets` (see `SITES.global` below) so a
 * single site can serve more than one Market via a locale subpath (`/pt-br`, `/es-mx`), not just
 * via a whole separate domain — this is a strict superset for any site with an empty
 * `markets: {}` (na, www behave byte-identically to before). Ported to this branch the same
 * day so both branches stay in sync on this file, per explicit instruction (the global branch
 * landed it first, alone, specifically to avoid touching this branch until it was verified).
 *
 * Auto-redirect logic (bot/preview/lighthouse skips, `/browsing_context_suggestions.json`
 * detection) is modeled on /Users/lx/Documents/website-builder/cornerstone's
 * `redirection.min.js`, minus its visible suggestion banner (silent redirect only, on the sites
 * that still do this — see `SITES[key].autoRedirect` below) and minus its multi-region app-data
 * matrix (`SITES` only ever lists real, launched destinations).
 *
 * Four-bucket redesign (2026-08-05, per /Users/lx/Documents/website-builder/ued-develop's
 * 跨子域名地区路由方案.md): `COUNTRY_TO_BUCKET` maps a detected/selected ISO country code to a
 * bucket key (na/eu/gl/www); `SITES` lists which buckets actually have a live site right now.
 * `resolveLiveSite()` is the seam between the two — a bucket with no live site yet (eu/gl, not
 * launched) falls back to `DEFAULT_BUCKET`. Launching eu/gl later is just adding a
 * `SITES.eu`/`SITES.gl` entry; the bucket table and fallback logic don't change.
 *
 * `SITES[key].autoRedirect` is a hardcoded per-site policy, not a runtime toggle: our own sites
 * (na today; eu/gl once launched) never auto-redirect a visitor — not on a fresh IP detection,
 * and not by re-enforcing an earlier preference cookie on repeat visits either. Only an
 * explicit choice (a manual picker; eventually the suggestion banner the methodology doc's §5
 * describes, not yet built) ever moves a visitor off one of our own sites. `www` keeps the
 * original both-layers-on behavior, with the fresh-detection layer still separately gated by
 * `window.__ofAutoIpRedirect` (set by snippets/site-region-router.liquid from
 * `settings.enable_ip_redirect` — a Theme Setting that exists only in the www theme now; this
 * repo removed it, see config/settings_schema.json).
 *
 * `region_pref`/`region_country` (renamed 2026-08-05 from `store_pref`/`store_country` as part
 * of the same redesign) are a parent-domain-shared cookie pair recording a visitor's own
 * choice — never a detection outcome, see `applyRegion()`'s `opts.record`. Session-only (no
 * Max-Age): closing the browser clears it. Renamed rather than just changing the values inside
 * the old cookie names specifically so any 1-year `store_pref`/`store_country` cookie already
 * sitting in a visitor's browser is simply never read by this code — no migration/compat logic
 * needed.
 */
(function () {
  'use strict';

  var BOT_PATTERN =
    '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis|APIs-Google|AdsBot-Google-Mobile|AdsBot-Google|Google-Safety)';

  var COOKIE_DOMAIN = '.ofhunger.com';
  var PREF_COOKIE = 'region_pref';
  var COUNTRY_COOKIE = 'region_country';

  // `markets`: optional map of ISO country code -> locale subpath, for a site that serves more
  // than one Market under the same domain (global's BR/MX at /pt-br, /es-mx). Empty on sites
  // that don't do this (na, www) — `matchMarket()`/`canonicalPath()` below treat an empty map
  // as "this site has no subpaths", so behavior for those sites is unchanged from before this
  // concept existed. A country only needs a `markets` entry on the site it actually resolves to
  // (see COUNTRY_TO_BUCKET below) — it's how `applyRegion()` builds the right subpath when
  // landing on that site, not a separate routing table of its own.
  // `defaultCountry`: the ISO code this site's own root already represents, for sites with no
  // `markets` of their own — na IS the US experience, full stop, so a visitor on
  // us.ofhunger.com with no Market subpath is implicitly "US" even though nothing in the URL
  // says so. Sites with more than one possible identity (www, or global outside its own BR/MX
  // subpaths) have no single default and omit this field — see `currentMarket()` below for how
  // the two cases differ (falls back to 'US'-the-literal-value vs `null`).
  var SITES = {
    na: {
      hostname: 'us.ofhunger.com',
      origin: 'https://us.ofhunger.com',
      autoRedirect: false,
      markets: {},
      defaultCountry: 'US',
    },
    www: { hostname: 'www.ofhunger.com', origin: 'https://www.ofhunger.com', autoRedirect: true, markets: {} },
    // 2026-09-22: global launched (MX/BR, subpaths /es-mx and /pt-br — see
    // snippets/footer-country-select.liquid). Before the `markets` field existed, switching
    // Markets here went through a parallel LOCALE_PATHS table duplicated in
    // footer-country-select.js instead of through this file at all — that duplication is what
    // caused the US/Other-Regions options to leak the current /es-mx or /pt-br prefix onto a
    // domain that doesn't have one (see applyRegion()'s own comment). `markets` here is now the
    // single source of truth both for that same-site subpath switch and for
    // currentSiteKey()/autoDetectAndApply() recognizing this domain as one of "our own sites".
    global: {
      hostname: 'global.ofhunger.com',
      origin: 'https://global.ofhunger.com',
      autoRedirect: false,
      markets: { BR: '/pt-br', MX: '/es-mx' },
    },
    // eu: add an entry here (same shape, autoRedirect: false, markets: {} unless it gets its own
    // locale subpaths too) once it actually launches.
  };

  // Deliberately minimal — only maps what's actually live today. Earlier drafts of this table
  // pre-populated CA -> na (wrong: na has no /ca and was never confirmed to serve Canada, see
  // region-routing-v2-plan.md's "实施时发现并修正的一处业务错误") and the full UK/EU/MX/BR ->
  // eu/gl set (harmless today since resolveLiveSite() falls back to DEFAULT_BUCKET for any
  // bucket without a live SITES entry, but still speculative code sitting in production ahead
  // of actual need). Simplified back down after that CA mistake: add a country here only when
  // there's a real SITES entry for its bucket to resolve to, not preemptively. MX/BR now have
  // one (global, added above 2026-09-22), so they're added here too — this table is the single
  // source of truth for both the footer selector's own switching (every option, including BR/MX,
  // goes through resolveBucket() via applyRegion()) and any future cross-site auto-detect/
  // suggestion feature (跨子域名地区路由方案.md §5, not built yet).
  var COUNTRY_TO_BUCKET = {
    US: 'na',
    MX: 'global',
    BR: 'global',
  };
  var DEFAULT_BUCKET = 'www'; // catch-all for every country not explicitly listed above

  function resolveBucket(countryCode) {
    return COUNTRY_TO_BUCKET[countryCode] || DEFAULT_BUCKET;
  }

  function resolveLiveSite(bucket) {
    return SITES[bucket] ? bucket : DEFAULT_BUCKET;
  }

  // Finds which of `site.markets` (if any) the given path is currently under — e.g. on
  // SITES.global, '/pt-br' or '/pt-br/products/foo' matches BR. Sites with no markets (na, www)
  // never match anything here, same as if this concept didn't exist for them.
  function matchMarket(site, path) {
    var markets = (site && site.markets) || {};
    for (var iso in markets) {
      var prefix = markets[iso];
      if (path === prefix || path.indexOf(prefix + '/') === 0) return { iso: iso, prefix: prefix };
    }
    return null;
  }

  // Which country a country-picker UI should preselect for the visitor's CURRENT site — 'BR' on
  // global.ofhunger.com/pt-br, 'US' anywhere on us.ofhunger.com (via `defaultCountry`, since
  // that site has no subpaths of its own to match), `null` on global's own root or on a site
  // with neither a market match nor a default (www) — the caller decides what null means (e.g.
  // footer-country-select.js's generic "OTHER" fallback). This is deliberately a different
  // question from `matchMarket()` used inside `applyRegion()`: that one is purely path-literal
  // (used to know what prefix to strip when building a redirect destination), this one folds in
  // a site's own implicit identity too — the two must stay separate functions so `applyRegion()`
  // never accidentally treats na's implicit "US" as something to strip out of a URL.
  function currentMarket() {
    var here = currentSiteKey();
    if (!here) return null;
    var site = SITES[here];
    var match = matchMarket(site, window.location.pathname);
    if (match) return match.iso;
    return site.defaultCountry || null;
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value) {
    // No max-age — session cookie by design, cleared when the browser closes (see top-of-file
    // comment: a recorded preference should never outlive the session that made it).
    document.cookie = name + '=' + encodeURIComponent(value) + '; domain=' + COOKIE_DOMAIN + '; path=/; Secure; SameSite=Lax';
  }

  function currentSiteKey() {
    var host = window.location.hostname;
    for (var key in SITES) {
      if (SITES[key].hostname === host) return key;
    }
    return null;
  }

  // Shared by autoDetectAndApply() and every country-picker UI, so all callers make the same
  // stay/redirect decision the same way. `opts.record` defaults to true (an explicit visitor
  // choice — a picker's change handler) and must be passed as `{ record: false }` for a silent
  // IP-detection outcome, which per the methodology doc never writes a preference ("探测从不写
  // 记录，只有访客自己的选择才写").
  function applyRegion(countryCode, opts) {
    var record = !opts || opts.record !== false;
    var targetKey = resolveLiveSite(resolveBucket(countryCode));
    var targetSite = SITES[targetKey];
    // Only non-empty when the target site actually has a subpath for this exact country (e.g.
    // 'BR' on SITES.global) — every other case (na, www, or an unmapped country falling
    // through to DEFAULT_BUCKET) stays '', identical to before `markets` existed.
    var targetPrefix = targetSite.markets[countryCode] || '';

    if (record) {
      setCookie(PREF_COOKIE, targetKey);
      setCookie(COUNTRY_COOKIE, countryCode);
    }

    var here = currentSiteKey();
    // `here` is null on unrecognized hosts (myshopify preview, localhost, staging) — never
    // navigate away from those, only ever from a real, recognized production host.
    if (!here) {
      return { targetKey: targetKey, redirected: false, destinationUrl: null };
    }

    var currentMatch = matchMarket(SITES[here], window.location.pathname);
    var currentPrefix = currentMatch ? currentMatch.prefix : '';

    // Same site AND same market prefix (both possibly '') means nothing would actually change —
    // e.g. picking "United States" while already on us.ofhunger.com, or re-picking the Market
    // you're already viewing on global. This generalizes the old `targetKey === here` check,
    // which implicitly assumed both prefixes were always '' back when no site had `markets`.
    if (here === targetKey && currentPrefix === targetPrefix) {
      return { targetKey: targetKey, redirected: false, destinationUrl: null };
    }

    // Strip whatever Market prefix the visitor is currently under (if any) before re-prefixing
    // with the target's — this is what keeps a mid-browse switch (e.g. /es-mx/products/foo ->
    // /pt-br/products/foo, or /es-mx/products/foo -> us.ofhunger.com/products/foo) from leaking
    // the wrong site's subpath onto the destination, which is exactly the bug this replaced (see
    // this file's own top comment).
    var canonical = currentMatch
      ? window.location.pathname.slice(currentMatch.prefix.length)
      : window.location.pathname;
    if (targetPrefix && canonical === '/') canonical = ''; // avoid a double slash, e.g. /es-mx/ -> /pt-br/

    var destinationUrl = targetSite.origin + targetPrefix + canonical + window.location.search;
    window.location.href = destinationUrl;
    return { targetKey: targetKey, redirected: true, destinationUrl: destinationUrl };
  }

  function isBot() {
    return new RegExp(BOT_PATTERN, 'i').test(navigator.userAgent);
  }

  function isPreview() {
    return window.location.hostname.indexOf('.shopifypreview.com') !== -1;
  }

  function isLighthouse() {
    return new URLSearchParams(window.location.search).get('isLighthouse') !== null;
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function fetchDetectedCountry() {
    return fetch('/browsing_context_suggestions.json')
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var country = data && data.detected_values && data.detected_values.country;
        return country && country.handle ? country.handle.toUpperCase() : null;
      })
      .catch(function () {
        return null;
      });
  }

  // Enforces a preference already on file — from an explicit manual pick via a country
  // selector (never from a plain IP detection, see applyRegion()'s `opts.record`) — on every
  // visit, redirecting if the current site doesn't match. Only reachable on sites where
  // `SITES[key].autoRedirect` is true (see autoDetectAndApply's gate) — today that's only www;
  // na/eu/gl never call this on load, since our own sites don't auto-redirect at all (see
  // top-of-file comment). Kept rather than deleted since www still needs it. Deliberately NOT
  // gated by window.__ofAutoIpRedirect: honoring an already-made choice is a different concern
  // from guessing one via IP for a first-time visitor. Returns true if it found and acted on
  // (or matched) a VALID existing preference, false if none exists yet (or the cookie holds
  // something that isn't a real SITES key — e.g. hand-edited in devtools to a raw country code
  // instead of "na"/"www" — treated the same as absent, rather than silently matching nothing
  // and swallowing the whole enforce+detect flow) so the caller falls through to fresh IP
  // detection instead.
  function enforceExistingPreference(here) {
    var cachedPref = getCookie(PREF_COOKIE);
    if (!SITES[cachedPref]) return false;

    if (cachedPref !== here) {
      window.location.href = SITES[cachedPref].origin + window.location.pathname + window.location.search;
    }
    return true;
  }

  function autoDetectAndApply() {
    if (isBot() || isPreview() || isLighthouse() || isDesignMode()) return;

    var here = currentSiteKey();
    if (!here) return; // unrecognized host — nothing to route between

    // Our own sites (na today; eu/gl once launched) never auto-redirect a visitor — neither by
    // enforcing an earlier preference nor by a fresh IP detection. Only an explicit choice
    // (a picker; eventually a suggestion banner) ever moves them off. See top-of-file comment.
    if (!SITES[here].autoRedirect) return;

    if (enforceExistingPreference(here)) return;

    if (!window.__ofAutoIpRedirect) return; // fresh IP lookup specifically stays opt-in (www only)

    fetchDetectedCountry().then(function (code) {
      if (!code) return; // detection failed — stay put; a detection outcome is never recorded anyway

      applyRegion(code, { record: false }); // a silent detection is never a recorded preference
    });
  }

  window.RegionRouter = {
    SITES: SITES,
    COUNTRY_COOKIE: COUNTRY_COOKIE,
    currentSiteKey: currentSiteKey,
    currentMarket: currentMarket,
    getCookie: getCookie,
    applyRegion: applyRegion,
    autoDetectAndApply: autoDetectAndApply,
  };

  // Always runs — autoDetectAndApply() internally gates itself: first on `SITES[here]
  // .autoRedirect` (na/eu/gl stop right there, every visit), then — only on sites where that's
  // true (www) — on window.__ofAutoIpRedirect for the fresh-IP-lookup branch specifically,
  // while enforcing an existing preference runs whenever that first gate passes (see
  // enforceExistingPreference's own comment). Country-picker UIs also call
  // window.RegionRouter.applyRegion() directly on their own change handlers, independent of any
  // of this.
  autoDetectAndApply();
})();
